import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Paper, Grid, List, ListItem, 
    ListItemText, ListItemSecondaryAction, IconButton, 
    CircularProgress, Alert, Snackbar, Divider, Chip, TablePagination, Switch
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentsIcon from '@mui/icons-material/Payments';
import { SettingsService } from '../../api/services/settingsService';
import { extractTotalCount } from '../../components/common/tableStyles';

export default function PaymentMethodsPage() {
    const [methods, setMethods] = useState([]);
    const [totalMethods, setTotalMethods] = useState(0);
    const [loading, setLoading] = useState(false);
    const [togglingId, setTogglingId] = useState(null);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    useEffect(() => {
        fetchPaymentMethods();
    }, [page, rowsPerPage]);

    const fetchPaymentMethods = async () => {
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || null;
            const data = await SettingsService.getPaymentMethods(tenantId, {
                page: page,
                limit: rowsPerPage
            });
            const methodsArray = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
            setMethods(methodsArray);
            setTotalMethods(extractTotalCount(data, methodsArray));
        } catch (error) {
            console.error('Failed to fetch payment methods:', error);
            setNotification({ open: true, message: 'Failed to load payment methods.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleToggleMethod = async (method) => {
        const methodId = method.id || method._id;
        const newStatus = !method.enabled;

        // Optimistic update
        setMethods(prev => prev.map(m => (m.id || m._id) === methodId ? { ...m, enabled: newStatus } : m));
        setTogglingId(methodId);

        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || null;
            await SettingsService.updatePaymentMethod(methodId, { enabled: newStatus }, tenantId);
            setNotification({ 
                open: true, 
                message: `${method.name || 'Payment method'} ${newStatus ? 'enabled' : 'disabled'} successfully`, 
                severity: 'success' 
            });
        } catch (error) {
            console.error('Failed to toggle payment method status:', error);
            // Revert optimistic update on failure
            setMethods(prev => prev.map(m => (m.id || m._id) === methodId ? { ...m, enabled: !newStatus } : m));
            setNotification({ 
                open: true, 
                message: error?.response?.data?.message || error.message || 'Failed to update payment method status', 
                severity: 'error' 
            });
        } finally {
            setTogglingId(null);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">Payment Methods</Typography>
            </Box>

            <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', p: 3 }}>
                <Grid container spacing={4}>
                    <Grid item xs={12} md={7}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold">Active Methods</Typography>
                            <IconButton onClick={fetchPaymentMethods} disabled={loading} size="small">
                                <RefreshIcon />
                            </IconButton>
                        </Box>
                        
                        <Paper sx={{ 
                            borderRadius: '12px', 
                            border: '1px solid #eaecf0', 
                            overflow: 'hidden',
                            boxShadow: '0 1px 3px 0 rgba(16, 24, 40, 0.05)'
                        }}>
                            <List sx={{ p: 0 }}>
                                {loading ? (
                                    <ListItem sx={{ justifyContent: 'center', py: 4 }}>
                                        <CircularProgress size={24} sx={{ mr: 2 }} />
                                        <Typography color="text.secondary">Loading methods...</Typography>
                                    </ListItem>
                                ) : methods.length > 0 ? (
                                    methods.map((method, idx) => {
                                        const methodId = method.id || method._id || idx;
                                        const isToggling = togglingId === (method.id || method._id);
                                        return (
                                            <ListItem key={methodId} divider={idx !== methods.length - 1} sx={{ py: 1.5 }}>
                                                <ListItemText 
                                                    primary={
                                                        <Typography variant="subtitle1" fontWeight={600}>
                                                            {method.name}
                                                        </Typography>
                                                    } 
                                                />
                                                <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {isToggling && <CircularProgress size={18} />}
                                                    <Switch
                                                        checked={Boolean(method.enabled)}
                                                        onChange={() => handleToggleMethod(method)}
                                                        disabled={isToggling || loading}
                                                        color="primary"
                                                        inputProps={{ 'aria-label': `${method.name} status toggle` }}
                                                    />
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        );
                                    })
                                ) : (
                                    <ListItem sx={{ py: 4, justifyContent: 'center' }}>
                                        <Typography color="text.secondary">No payment methods configured</Typography>
                                    </ListItem>
                                )}
                            </List>
                            <TablePagination
                                component="div"
                                count={methods.length === 0 ? 0 : totalMethods}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[20, 50, 100]}
                            />
                        </Paper>
                    </Grid>
                    
                    <Grid item xs={12} md={5}>
                        <Box sx={{ p: 3, bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <PaymentsIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6" fontWeight="bold">Information</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Manage accepted payment options for guest transactions, invoices, and folio billing.
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">Configured Methods:</Typography>
                                <Typography variant="body2" fontWeight="bold">{methods.length}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">Active / Enabled:</Typography>
                                <Typography variant="body2" fontWeight="bold" color="success.main">
                                    {methods.filter(m => m.enabled).length}
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 3 }}>
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    Toggle payment methods on or off. Only enabled payment methods will be selectable during reservation check-outs and billing.
                                </Alert>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Snackbar 
                open={notification.open} 
                autoHideDuration={6000} 
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={notification.severity} sx={{ width: '100%', borderRadius: 2 }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
